import { parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js/max";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";

export type MessengerType = "telegram" | "whatsapp" | "teams" | "wechat" | "line" | "qq" | "other";
export type ContactInputMode = "username" | "phone";
export type CountryCode = ReturnType<typeof getCountries>[number];

export const DEFAULT_MESSENGER: MessengerType = "telegram";
export const DEFAULT_CONTACT_INPUT_MODE: ContactInputMode = "username";
export const DEFAULT_COUNTRY: CountryCode = "US";

export const MESSENGER_PREFIX: Record<MessengerType, string> = {
    telegram: "tg",
    whatsapp: "whatsapp",
    teams: "teams",
    wechat: "wechat",
    line: "line",
    qq: "qq",
    other: "other",
};

export const MESSENGER_OPTIONS: { key: MessengerType; i18nKey: string }[] = [
    { key: "telegram", i18nKey: "pages.registration.messenger.telegram" },
    { key: "whatsapp", i18nKey: "pages.registration.messenger.whatsapp" },
    { key: "teams", i18nKey: "pages.registration.messenger.teams" },
    { key: "wechat", i18nKey: "pages.registration.messenger.wechat" },
    { key: "line", i18nKey: "pages.registration.messenger.line" },
    { key: "qq", i18nKey: "pages.registration.messenger.qq" },
    { key: "other", i18nKey: "pages.registration.messenger.other" },
];

export const MESSENGER_MODES: Record<MessengerType, ContactInputMode[]> = {
    teams: ["username"],
    whatsapp: ["phone"],
    telegram: ["username", "phone"],
    wechat: ["username", "phone"],
    line: ["username", "phone"],
    qq: ["username", "phone"],
    other: ["username", "phone"],
};

export const USERNAME_PLACEHOLDERS: Record<MessengerType, string> = {
    telegram: "pages.registration.placeholder.telegram",
    teams: "pages.registration.placeholder.teams",
    wechat: "pages.registration.placeholder.wechat",
    line: "pages.registration.placeholder.line",
    qq: "pages.registration.placeholder.qq",
    other: "pages.registration.placeholder.username-generic",
    whatsapp: "pages.registration.placeholder.phone",
};

const PREFIX_MESSENGER = Object.fromEntries(
    Object.entries(MESSENGER_PREFIX).map(([messenger, prefix]) => [prefix, messenger]),
) as Record<string, MessengerType>;

const COUNTRY_METADATA_PATTERN = /;\s*country=([a-z]{2})\s*$/i;
const COUNTRIES = getCountries();

const parseContactValue = (rawValue: string) => {
    const countryMatch = rawValue.match(COUNTRY_METADATA_PATTERN);

    return {
        value: rawValue.replace(COUNTRY_METADATA_PATTERN, "").trim(),
        country: countryMatch?.[1]?.toUpperCase() as CountryCode | undefined,
    };
};

const hasSharedCallingCode = (country: CountryCode) => {
    const callingCode = getCountryCallingCode(country);

    return COUNTRIES.some(
        (candidate) => candidate !== country && getCountryCallingCode(candidate) === callingCode,
    );
};

export const formatMessengerContact = (
    value: string,
    messenger: MessengerType,
    country?: CountryCode,
) => {
    if (!value) return "";

    const countryMetadata =
        country && value.startsWith("+") && hasSharedCallingCode(country)
            ? `;country=${country}`
            : "";

    return `${MESSENGER_PREFIX[messenger]}: ${value}${countryMetadata}`;
};

export const getMessengerContactValue = (contact: string) =>
    parseContactValue(contact.match(/^[^:]+:\s*(.+)$/)?.[1]?.trim() ?? "").value;

export const isValidMessengerContactPhone = (contact: string) => {
    const value = getMessengerContactValue(contact);
    if (!/^\+\d+$/.test(value)) return true;

    return isValidPhoneNumber(value);
};

export const parseMessengerContact = (contact: string) => {
    const [prefix, ...valueParts] = contact.split(":");
    const { value, country: storedCountry } = parseContactValue(valueParts.join(":").trim());
    const messenger = PREFIX_MESSENGER[prefix?.trim()] ?? DEFAULT_MESSENGER;
    const parsedPhone = parsePhoneNumberFromString(value);
    const hasStoredCountry =
        storedCountry &&
        COUNTRIES.includes(storedCountry) &&
        value.startsWith(`+${getCountryCallingCode(storedCountry)}`);
    const country = hasStoredCountry
        ? storedCountry
        : (parsedPhone?.country as CountryCode | undefined) ?? DEFAULT_COUNTRY;

    if (parsedPhone?.country || value.startsWith("+")) {
        const callingCode = `+${getCountryCallingCode(country)}`;
        const normalized = value.replace(/[^\d+]/g, "");
        const phoneDigits = normalized.startsWith(callingCode)
            ? normalized.slice(callingCode.length)
            : parsedPhone?.nationalNumber ?? normalized.replace(/^\+/, "");

        return {
            messenger,
            inputMode: "phone" as ContactInputMode,
            country,
            phoneDigits,
        };
    }

    return {
        messenger,
        inputMode: "username" as ContactInputMode,
        country: DEFAULT_COUNTRY,
        phoneDigits: "",
    };
};
